import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Flame } from "lucide-react";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import { showSuccess } from "shared/toast";
import { apiErrorMessage } from "../apiError";
import { postWarmLeadAction, type WarmLead, type WarmLeadAction } from "../api/OutreachTouchesApi";
import { announceDueChange } from "../modal/useDueCounts";
import { OUTCOME_LABEL } from "./ConversationActions";
import { formatCalendarDate } from "../outreachDates";

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

export const KIND_LABEL: Record<WarmLead["kind"], string> = {
  interested: "Interested",
  interested_other_rfp: "Interested, not that RFP",
  teaming: "Partner (teaming)",
};

function dueLabel(days: number | null): { text: string; tone: "danger" | "warning" | "neutral" } {
  if (days === null) return { text: "No due date", tone: "neutral" };
  if (days < 0) return { text: "RFP due date passed", tone: "danger" };
  if (days === 0) return { text: "Due today", tone: "danger" };
  return { text: `Due in ${days} day${days === 1 ? "" : "s"}`, tone: days <= 7 ? "warning" : "neutral" };
}

interface Props {
  lead: WarmLead;
  onChanged: () => void;
  /** Skipped / not-a-fit rows (the "closed" view) offer Restore instead of the three actions. */
  closed?: boolean;
}

/** A company that already wants to work with us, matched to a newer RFP: what they said, why it fits, and the three things a person can do. */
export default function WarmLeadCard({ lead, onChanged, closed }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showDraft, setShowDraft] = useState(false);
  const due = dueLabel(lead.days_left);
  const who = lead.contact_name || lead.contact_email || "their contact";

  async function run(action: WarmLeadAction, done: string) {
    setBusy(true);
    setError("");
    try {
      const result = await postWarmLeadAction(lead.id, action);
      showSuccess(done);
      announceDueChange();
      if (action === "write" && result.conversation) {
        // The conversation carries the draft itself (until a message of ours is logged), so a reload still shows it.
        navigate(`${crmBase()}/due-today/conversations?c=${result.conversation.id}`);
        return;
      }
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that. Reload and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="rounded-xl border border-border bg-surface p-4 space-y-3" data-testid={`warm-lead-${lead.id}`}>
      <header className="flex flex-wrap items-center gap-2">
        <Flame className="w-4 h-4 text-orange shrink-0" aria-hidden />
        <Link to={`${crmBase()}/companies/${lead.prime_company_id}`} className="font-semibold text-foreground hover:underline">{lead.company_name ?? "Company"}</Link>
        <span className="text-sm text-muted-foreground">{who}</span>
        <StatusPill tone={lead.kind === "teaming" ? "teal" : "orange"} size="sm">{KIND_LABEL[lead.kind]}</StatusPill>
        {lead.fit_status === "hold" && <StatusPill tone="warning" size="sm" title={lead.fit_note ?? undefined}>Verify first</StatusPill>}
        {lead.fit_status === "conditions" && <StatusPill tone="info" size="sm" title={lead.fit_note ?? undefined}>Fits with conditions</StatusPill>}
        <span className="ml-auto"><StatusPill tone={due.tone} size="sm">{due.text}</StatusPill></span>
      </header>

      <p className="text-sm text-foreground">
        New RFP: <Link to={`${crmBase()}?opportunity_id=${lead.opportunity_id}`} className="font-medium hover:underline">{lead.rfp_title ?? `RFP ${lead.opportunity_id}`}</Link>
        {lead.rfp_deadline && <span className="text-muted-foreground"> · due {formatCalendarDate(lead.rfp_deadline.slice(0, 10))}</span>}
      </p>

      {lead.fit_note && <p className="text-xs text-muted-foreground" data-testid="fit-note">{lead.fit_note}</p>}

      <div className="rounded-lg bg-surface-muted px-3 py-2 text-sm space-y-1">
        <p className="text-xs font-medium text-muted-foreground">
          {lead.source_rfp_title ? `What they said on ${lead.source_rfp_title}` : "What they said"}{lead.source_note ? ` · ${lead.source_note}` : ""}
        </p>
        <p className="whitespace-pre-wrap text-foreground">{lead.their_last_message ?? "No message logged."}</p>
      </div>

      {lead.other_warm.length > 1 && (
        <ul className="text-xs text-muted-foreground list-disc pl-4" aria-label="Every warm conversation of this company">
          {lead.other_warm.map((w) => (
            <li key={w.conversation_id}>{OUTCOME_LABEL[w.outcome as keyof typeof OUTCOME_LABEL] ?? w.outcome} on {w.rfp_title ?? `RFP ${w.opportunity_id}`}{w.contact_name ? ` (${w.contact_name})` : ""}{w.note ? `: ${w.note}` : ""}</li>
          ))}
        </ul>
      )}

      <div>
        <button type="button" onClick={() => setShowDraft((v) => !v)} aria-expanded={showDraft}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary underline underline-offset-2">
          {showDraft ? <ChevronDown className="w-3.5 h-3.5" aria-hidden /> : <ChevronRight className="w-3.5 h-3.5" aria-hidden />}
          {showDraft ? "Hide the suggested draft" : "Preview the suggested draft"}
        </button>
        {showDraft && (lead.suggested_message
          ? <p className="mt-2 whitespace-pre-wrap rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground" data-testid="draft-preview">{lead.suggested_message}</p>
          : <p className="mt-2 text-xs text-muted-foreground" data-testid="draft-preview">No draft was written for this one. The message box will be empty.</p>)}
      </div>

      {error && <Alert tone="danger" role="alert">{error}</Alert>}

      {lead.stale_reason && (
        <Alert tone="warning" role="status"><span data-testid="stale-notice"><strong>No longer applies.</strong> {lead.stale_text} It comes back by itself if that changes.</span></Alert>
      )}

      <div className="flex flex-wrap gap-2">
        {lead.stale_reason ? null : lead.state === "written" && lead.written_conversation_id ? (
          <Link to={`${crmBase()}/due-today/conversations?c=${lead.written_conversation_id}`} className="text-sm font-medium text-primary underline underline-offset-2">Open the conversation</Link>
        ) : closed ? (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => run("restore", "Back in Warm leads")}>Restore</Button>
        ) : (
          <>
            <Button variant="primary" size="sm" disabled={busy} onClick={() => run("write", "Conversation opened")}>Write to them</Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run("not_a_fit", "Marked not a fit for them")}>Not a fit for them</Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run("skip", "Skipped for now")}>Skip</Button>
          </>
        )}
      </div>
    </article>
  );
}
